import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCluster } from '../hooks/useCluster';
import StatusDropdown from '../components/StatusDropdown';
import { CompetitorBadges } from '../components/CompetitorBadge';
import RegionalBreakdown from '../components/RegionalBreakdown';
import TicketCard from '../components/TicketCard';
import api from '../api/client';
import StatusBadge from '../components/StatusBadge';
import { getAgentRole, canWrite, canExport } from '../lib/roles';

const API_BASE = process.env.REACT_APP_API_URL || '/api';


function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function CopyButton({ text, label, style: btnStyle }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 12px', fontSize: 12, fontWeight: 600,
        color: copied ? 'var(--color-success)' : 'var(--text-muted)',
        background: copied ? 'var(--color-success-bg)' : 'var(--bg-tertiary)',
        border: '1px solid var(--border-color)', borderRadius: 6,
        cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
        ...btnStyle,
      }}
    >
      {copied ? (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          {label}
        </>
      )}
    </button>
  );
}

function StatCard({ value, label, color }) {
  return (
    <div style={{
      flex: 1, minWidth: 100,
      background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
      borderRadius: 10, padding: '12px 14px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--text-primary)', lineHeight: 1.2 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

function FeishuButton({ clusterId }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error | unconfigured
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.feishuStatus().then(res => {
      if (!res.data.configured) setState('unconfigured');
    }).catch((err) => {
      if (import.meta.env?.DEV) console.error('Feishu status check failed:', err);
    });
  }, []);

  if (state === 'unconfigured') return null;

  const handleSync = async () => {
    setState('loading');
    try {
      const res = await api.feishuSyncCluster(clusterId);
      setMessage(res.data.message);
      setState('done');
      setTimeout(() => setState('idle'), 3000);
    } catch (err) {
      setMessage(err.message || 'Sync failed');
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  };

  const colors = {
    idle: { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: 'var(--border-color)' },
    loading: { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: 'var(--border-color)' },
    done: { bg: 'var(--color-success-bg)', color: 'var(--color-success)', border: 'var(--color-success)' },
    error: { bg: 'var(--color-error-bg)', color: 'var(--color-error)', border: 'var(--color-error)' },
  };
  const c = colors[state] || colors.idle;

  return (
    <button
      onClick={handleSync}
      disabled={state === 'loading'}
      title={message || 'Push to Feishu Bitable'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 12px', fontSize: 12, fontWeight: 600,
        color: c.color, background: c.bg,
        border: `1px solid ${c.border}`, borderRadius: 6,
        cursor: state === 'loading' ? 'wait' : 'pointer',
        fontFamily: 'inherit', transition: 'all 0.15s',
        opacity: state === 'loading' ? 0.7 : 1,
      }}
    >
      {state === 'loading' ? (
        'Syncing...'
      ) : state === 'done' ? (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Synced
        </>
      ) : state === 'error' ? (
        'Failed'
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
          Push to Feishu
        </>
      )}
    </button>
  );
}

function KlaviyoButton({ clusterId }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [message, setMessage] = useState('');

  const handleSync = async () => {
    setState('loading');
    try {
      const res = await api.klaviyoSyncCluster(clusterId);
      const d = res.data || {};
      setMessage(`${d.recipient_count} → "${d.list_name}"${d.list_created ? ' (new list)' : ''}`);
      setState('done');
      setTimeout(() => setState('idle'), 4000);
    } catch (err) {
      setMessage(
        err.message?.includes('KLAVIYO_API_KEY')
          ? 'Klaviyo not configured'
          : err.message || 'Sync failed'
      );
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  };

  const colors = {
    idle: { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: 'var(--border-color)' },
    loading: { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: 'var(--border-color)' },
    done: { bg: 'var(--color-success-bg)', color: 'var(--color-success)', border: 'var(--color-success)' },
    error: { bg: 'var(--color-error-bg)', color: 'var(--color-error)', border: 'var(--color-error)' },
  };
  const c = colors[state] || colors.idle;

  return (
    <button
      onClick={handleSync}
      disabled={state === 'loading'}
      title={message || 'Build a Klaviyo list from this cluster’s requesters (does not send email)'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 12px', fontSize: 12, fontWeight: 600,
        color: c.color, background: c.bg,
        border: `1px solid ${c.border}`, borderRadius: 6,
        cursor: state === 'loading' ? 'wait' : 'pointer',
        fontFamily: 'inherit', transition: 'all 0.15s',
        opacity: state === 'loading' ? 0.7 : 1,
      }}
    >
      {state === 'loading' ? (
        'Pushing…'
      ) : state === 'done' ? (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {message || 'Pushed'}
        </>
      ) : state === 'error' ? (
        message || 'Failed'
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16v16H4z" />
            <polyline points="22 6 12 13 2 6" />
          </svg>
          Push to Klaviyo
        </>
      )}
    </button>
  );
}

export function ClusterDetail() {
  const { id } = useParams();
  const { data, regional, loading, error, updateStatus } = useCluster(id);
  const role = getAgentRole();
  const mayExport = canExport(role);
  const mayWrite = canWrite(role);
  const [exportError, setExportError] = useState('');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-coros-text-muted">Loading cluster...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-coros-status-error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const clusterUrl = `${window.location.origin}/clusters/${data.id}`;
  const shareText = `"${data.name}" — ${data.ticket_count} requests${data.trend_growth_rate ? ` (${data.trend_growth_rate > 0 ? '+' : ''}${data.trend_growth_rate}%)` : ''} | Status: ${data.status?.replace('_', ' ')} | ${clusterUrl}`;
  const trendColor = data.trend_growth_rate > 0 ? 'var(--color-success)' : data.trend_growth_rate < 0 ? 'var(--color-error)' : 'var(--text-muted)';
  const aiConfidence = data.ai_confidence ? `${Math.round(data.ai_confidence * 100)}%` : 'N/A';

  const handleExportCsv = async () => {
    setExportError('');
    try {
      // Authenticated blob download — a bare anchor href can't send the JWT header (→401).
      await api.exportClusterEmails(data.id);
    } catch (err) {
      setExportError('Failed to export CSV');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb + Share buttons */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Link to="/" className="text-coros-red hover:text-coros-red-hover text-sm">
          ← Back to Dashboard
        </Link>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <CopyButton text={clusterUrl} label="Copy Link" />
          <CopyButton text={shareText} label="Share" />
          {mayWrite && <FeishuButton clusterId={data.id} />}
          {mayExport && <KlaviyoButton clusterId={data.id} />}
          {mayExport && (
            <button
              onClick={handleExportCsv}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                color: 'var(--text-muted)',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)', borderRadius: 6,
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--coros-red)';
                e.currentTarget.style.borderColor = 'var(--coros-red)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-muted)';
                e.currentTarget.style.borderColor = 'var(--border-color)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download CSV
            </button>
          )}
        </div>
      </div>

      {/* Export error */}
      {exportError && (
        <div style={{
          padding: '10px 12px',
          marginBottom: 16,
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: 6,
          fontSize: 12,
          color: 'var(--color-error)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        }}>
          {exportError}
        </div>
      )}

      {/* Header: Name + Status + Note */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <h1 className="text-2xl font-bold text-coros-text-primary" style={{ margin: 0 }}>{data.name}</h1>
          {data.is_emerging_trend && (
            <span className="badge bg-red-50 text-coros-red">🔥 Emerging</span>
          )}
          {data.competitor_mentions && data.competitor_mentions.length > 0 && (
            <CompetitorBadges competitors={data.competitor_mentions} />
          )}
        </div>
        {data.description && (
          <p style={{ color: 'var(--text-muted)', margin: '0 0 12px' }}>{data.description}</p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {mayWrite
            ? <StatusDropdown currentStatus={data.status} onUpdate={updateStatus} />
            : <StatusBadge status={data.status} />}
          {data.status_note && (
            <div style={{
              fontSize: 12, color: 'var(--text-secondary)',
              background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
              borderRadius: 6, padding: '6px 10px', maxWidth: 500,
            }}>
              {data.status_note}
              {data.status_updated_by && (
                <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                  — {data.status_updated_by}, {timeAgo(data.status_updated_at)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Decision stat cards */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard value={data.ticket_count} label="Requests" />
        <StatCard
          value={data.trend_growth_rate != null ? `${data.trend_growth_rate > 0 ? '+' : ''}${data.trend_growth_rate}%` : '—'}
          label="Trend"
          color={trendColor}
        />
        <StatCard
          value={data.rank ? `#${data.rank}` : '—'}
          label="Rank"
          color="var(--coros-red)"
        />
        <StatCard value={aiConfidence} label="AI Confidence" />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Sample Tickets */}
          <div>
            <h2 className="text-xl font-semibold text-coros-text-primary mb-4">
              Tickets
              <span className="text-coros-text-muted font-normal text-base ml-2">
                {data.sample_tickets?.length < data.ticket_count
                  ? `showing ${data.sample_tickets?.length || 0} of ${data.ticket_count}`
                  : `${data.sample_tickets?.length || 0}`}
              </span>
            </h2>
            <div className="space-y-4">
              {data.sample_tickets?.map((ticket) => (
                <TicketCard key={ticket.id} ticket={ticket} showCluster={false} />
              ))}
              {(!data.sample_tickets || data.sample_tickets.length === 0) && (
                <p className="text-coros-text-muted">No sample tickets available</p>
              )}
            </div>
            <div className="mt-4">
              <Link
                to={`/search?cluster_id=${data.id}`}
                className="text-coros-red hover:text-coros-red-hover text-sm"
              >
                View all tickets →
              </Link>
            </div>
          </div>

          {/* Ranking History */}
          {data.ranking_history && data.ranking_history.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-coros-text-primary mb-4">
                Ranking History
              </h2>
              <div className="card overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-coros-text-muted text-sm border-b border-coros-border">
                      <th className="pb-3 font-medium">Period</th>
                      <th className="pb-3 font-medium text-center">Rank</th>
                      <th className="pb-3 font-medium text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ranking_history.map((row, index) => (
                      <tr key={index} className="border-b border-coros-border-light last:border-0">
                        <td className="py-3 text-coros-text-secondary">{row.period}</td>
                        <td className="py-3 text-center">
                          <span className="text-coros-red font-medium">#{row.rank}</span>
                        </td>
                        <td className="py-3 text-right text-coros-text-secondary">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Regional Breakdown */}
          {regional && <RegionalBreakdown data={regional} />}

          {/* Cluster Info */}
          <div className="card">
            <h3 className="text-lg font-semibold text-coros-text-primary mb-4">Cluster Info</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-coros-text-muted">Type</span>
                <span className="text-coros-text-primary capitalize">
                  {data.cluster_type?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-coros-text-muted">Created</span>
                <span className="text-coros-text-primary">
                  {data.created_at ? new Date(data.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              {data.ai_confidence && (
                <div className="flex justify-between">
                  <span className="text-coros-text-muted">AI Confidence</span>
                  <span className="text-coros-text-primary">
                    {Math.round(data.ai_confidence * 100)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClusterDetail;
